import {BrandAppDownload} from '../../shared/brand/BrandAppDownload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDownCircle, ArrowLeft, ArrowRight, Check, Coins, Crown, History, LayoutGrid, Lock, Medal, ShieldCheck, Sparkles, Target, Ticket, UsersRound } from 'lucide-react';
import type { MouseEvent as ReactMouseEvent, RefObject, TouchEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { GameCode, Room } from '../../entities/room/types';
import { roomOnlineCount } from '../../entities/room/types';
import { userVipLevel, userWalletBrl, type PlayerUser } from '../../entities/user/types';
import { useAppStore, type PlayerRoute } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { fetchMilharStatus } from '../../features/milhar/api';
import { poolRoundsLine } from '../../shared/milhar/poolView';
import { liveMilharTiers, upcomingMilharTiers, type MilharTier, type MilharTierId } from '../../shared/milhar/tiers';
import { fetchGamePresence, fetchRooms } from '../../features/lobby/api';
import { fetchArchiveRounds, type ArchiveRound } from '../../features/archive/api';
import { fetchRaffleData } from '../../features/wallet/api';
import { raffleReadsEnabled, raffleViewerMode } from '../../shared/raffle/viewerMode';
import { DEFAULT_LEADERBOARD_PERIOD, fetchLeaderboard, fetchSocialFollowing } from '../../features/leaderboard/api';
import { poolOf } from '../../shared/game/rafflePrize';
import { roundAnchorTimeZone } from '../../shared/game/roundLabel';
import { BrandOwl, BrandWordmark } from '../../shared/brand/BrandOwl';
import { WalletChip } from '../../shared/wallet/WalletChip';
import { GAME_LABELS, gameNameKey } from '../../shared/game/betOptions';
import { GameIcon } from '../../shared/game/GameIcon';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import type { MessageKey } from '../../shared/i18n/messages';
import { fmtAmount, fmtCurrency, fmtInt, fmtMoney, fmtPercent } from '../../shared/lib/format';
import { readStorage, writeStorage } from '../../shared/lib/storage';
import { useHistoryLayers } from '../../shared/navigation/useHistoryLayers';
import { useCountryStore } from '../../shared/country/countryStore';
import { marketCopyKey } from '../../shared/country/marketCopy';

const PULL_REFRESH_THRESHOLD = 64;
const LANDING_SEEN_KEY = 'fd:landingSeen';
const LAST_ROOM_KEY = 'sorteia:lastRoom';
const ROOM_ENTRY_INTENT_KEY = 'sorteia:roomEntryIntent';
type PullState = 'idle' | 'pulling' | 'armed' | 'refreshing';

export function LobbyPage() {
  const [selectedGame, setSelectedGame] = useState<GameCode | null>(null);

  // The room directory is a full screen, but it was only useState — so the system
  // back button skipped it and left the hall entirely (and on a cold start, the
  // app). One layer deep is all it needs.
  useHistoryLayers([
    { id: 'lobby.rooms', open: !!selectedGame, onClose: () => setSelectedGame(null) },
  ]);
  const [landingSeen, setLandingSeen] = useState(() => readStorage(typeof window === 'undefined' ? undefined : window.localStorage, LANDING_SEEN_KEY) === '1');
  const [pullState, setPullState] = useState<PullState>('idle');
  const [pullDistance, setPullDistance] = useState(0);
  const pullStartY = useRef(0);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const logout = useAuthStore((state) => state.logout);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const setRoute = useAppStore((state) => state.setRoute);
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  useI18nStore((state) => state.language);
  const queryClient = useQueryClient();

  // Browse entries land on the archive LIST — clear any persisted selection so
  // opening it never auto-pops a stale round detail (only a URL deep-link or a
  // tapped round card inside the archive opens a specific draw).
  const openArchiveList = () => {
    useAppStore.getState().setActiveArchiveRoundNumber(null, { replace: true });
    setRoute('archive');
  };

  const roomsQuery = useQuery({
    queryKey: ['rooms'],
    queryFn: fetchRooms,
    refetchInterval: 60_000,
  });
  const presenceQuery = useQuery({
    queryKey: ['lobby-game-presence'],
    queryFn: fetchGamePresence,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 8_000,
  });
  // Recent settled rounds — draws are global per game, so one query feeds
  // every room card's ÚLTIMOS trail with real data (UX review round-2 catch).
  const trailQuery = useQuery({
    queryKey: ['lobby-trail'],
    queryFn: () => fetchArchiveRounds(brTodayDate(), 40),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const trailRounds = useMemo(() => {
    return (trailQuery.data?.rounds || [])
      .filter((round) => String(round.status || '').toUpperCase() === 'SETTLED' && round.games)
      .sort((a, b) => b.round_number - a.round_number)
      .slice(0, 8);
  }, [trailQuery.data]);


  // Live teaser for the Sorteios marquee — shares the ['raffle', token] cache
  // with the Sorteios page, so it's usually warm and adds no real cost.
  const raffleQuery = useQuery({
    queryKey: ['raffle', token],
    queryFn: () => fetchRaffleData(token || ''),
    enabled: raffleReadsEnabled(raffleViewerMode(token, guestMode)),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const raffleEvent = raffleQuery.data?.event || null;
  const raffleStatus = String(raffleEvent?.status || '').toLowerCase();
  const raffleOpen = raffleStatus === 'registering';
  // A locked draw has not resolved: it still has a pool and a draw time, so the
  // marquee keeps showing them under a "full" framing rather than dropping back
  // to the generic hint the moment the last seat sells.
  const raffleHeld = ['locked', 'full', 'settling'].includes(raffleStatus);
  const rafflePool = poolOf(raffleEvent?.prize_brackets);
  const raffleNextTime = raffleEvent?.scheduled_at ? hhmmBRT(raffleEvent.scheduled_at) : '';
  // Live teaser: "Próximo às 10:58 · [R$220] em prêmios" — the amount is styled
  // (gold mono, money semantic), so split the i18n template on %v% to wrap it.
  const raffleTeaserParts = (raffleOpen || raffleHeld) && rafflePool > 0
    ? t(raffleOpen ? 'sorteiosLobbyNext' : 'sorteiosLobbyFull').replace('%t%', raffleNextTime).split('%v%')
    : null;

  const rooms = roomsQuery.data?.rooms || [];
  const roomsReliable = !roomsQuery.isError;
  const vipLevel = userVipLevel(user);
  const groupedRooms = useMemo(() => groupRooms(rooms), [rooms]);

  useEffect(() => {
    if (!roomsReliable) setSelectedGame(null);
  }, [roomsReliable]);

  function goToAuth() {
    if (guestMode) clearGuest();
    logout();
    setRoute('auth');
  }

  function enterRoom(room: Room) {
    writeStorage(typeof window === 'undefined' ? undefined : window.localStorage, LAST_ROOM_KEY, room.id);
    useAppStore.getState().setActiveRoomId(room.id);
    useAppStore.getState().setRoute('room');
  }

  function openGameChart(game: GameCode) {
    const candidates = rooms
      .filter((room) => room.game === game && roomGate(room, vipLevel, t).ok)
      .sort((a, b) => {
        const aAffordable = guestMode || userWalletBrl(user) >= Number(a.chipValuesBrl?.[0] || 0) ? 1 : 0;
        const bAffordable = guestMode || userWalletBrl(user) >= Number(b.chipValuesBrl?.[0] || 0) ? 1 : 0;
        return bAffordable - aAffordable || Number(b.thisRoundBets || 0) - Number(a.thisRoundBets || 0) || Number(b.online || 0) - Number(a.online || 0);
      });
    const room = candidates[0];
    if (!room) {
      setSelectedGame(game);
      return;
    }
    writeStorage(typeof window === 'undefined' ? undefined : window.sessionStorage, ROOM_ENTRY_INTENT_KEY, 'trend');
    enterRoom(room);
  }

  function pullStart(event: TouchEvent<HTMLDivElement>) {
    if (pullState === 'refreshing' || event.currentTarget.scrollTop > 0) return;
    pullStartY.current = event.touches[0]?.clientY || 0;
    setPullState('pulling');
  }

  function pullMove(event: TouchEvent<HTMLDivElement>) {
    if (pullState !== 'pulling' && pullState !== 'armed') return;
    if (event.currentTarget.scrollTop > 0) {
      resetPull();
      return;
    }
    const dy = (event.touches[0]?.clientY || 0) - pullStartY.current;
    if (dy <= 0) {
      setPullDistance(0);
      setPullState('pulling');
      return;
    }
    const nextDistance = Math.min(140, dy * 0.55);
    if (nextDistance > 8) event.preventDefault();
    setPullDistance(nextDistance);
    setPullState(nextDistance >= PULL_REFRESH_THRESHOLD ? 'armed' : 'pulling');
  }

  function pullEnd() {
    if (pullState === 'armed') {
      refreshLobby();
      return;
    }
    if (pullState !== 'refreshing') resetPull();
  }

  function resetPull() {
    setPullState('idle');
    setPullDistance(0);
  }

  async function refreshLobby() {
    setPullState('refreshing');
    setPullDistance(56);
    await Promise.all([
      roomsQuery.refetch(),
      token ? queryClient.invalidateQueries({ queryKey: ['me', token] }) : Promise.resolve(),
    ]);
    window.setTimeout(resetPull, 180);
  }

  const pullLabel = pullState === 'refreshing' ? t('refreshing') : pullState === 'armed' ? t('releaseToRefresh') : t('pullToRefresh');

  // Legacy binds mouse drag alongside touch for pull-to-refresh (hall.html:69-76).
  const mousePulling = useRef(false);
  function pullMouseStart(event: ReactMouseEvent<HTMLDivElement>) {
    if (pullState === 'refreshing' || event.currentTarget.scrollTop > 0) return;
    mousePulling.current = true;
    pullStartY.current = event.clientY;
    setPullState('pulling');
  }
  function pullMouseMove(event: ReactMouseEvent<HTMLDivElement>) {
    if (!mousePulling.current) return;
    if (pullState !== 'pulling' && pullState !== 'armed') return;
    if (event.currentTarget.scrollTop > 0) { mousePulling.current = false; resetPull(); return; }
    const dy = event.clientY - pullStartY.current;
    if (dy <= 0) { setPullDistance(0); setPullState('pulling'); return; }
    const nextDistance = Math.min(140, dy * 0.55);
    setPullDistance(nextDistance);
    setPullState(nextDistance >= PULL_REFRESH_THRESHOLD ? 'armed' : 'pulling');
  }
  function pullMouseEnd() {
    if (!mousePulling.current) return;
    mousePulling.current = false;
    pullEnd();
  }

  if (selectedGame) {
    return (
      <GameRoomDirectory
        game={selectedGame}
        rooms={rooms.filter((room) => room.game === selectedGame)}
        vipLevel={vipLevel}
        walletBrl={userWalletBrl(user)}
        guestMode={guestMode}
        onBack={() => setSelectedGame(null)}
        onEnter={enterRoom}
      />
    );
  }

  return (
    <section className="lobby-screen ds-screen">
      <header className="ds-header shrink-0">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <BrandOwl size={34} />
          <div className="min-w-0">
            <BrandWordmark height={18} />
          </div>
        </div>
        {!guestMode ? (
          <WalletChip onOpen={() => setRoute('wallet')} balance={userWalletBrl(user)} />
        ) : (
          <button className="h-9 px-3 rounded-full bg-gold-500/10 border border-gold-500/40 text-gold-300 text-[12px] font-bold shrink-0" type="button" onClick={goToAuth}>
            {t('login')}
          </button>
        )}
      </header>
      <BrandAppDownload />

      <div
        className="flex-1 min-h-0 overflow-y-auto no-scrollbar relative"
        onTouchStart={pullStart}
        onTouchMove={pullMove}
        onTouchEnd={pullEnd}
        onTouchCancel={pullEnd}
        onMouseDown={pullMouseStart}
        onMouseMove={pullMouseMove}
        onMouseUp={pullMouseEnd}
        onMouseLeave={pullMouseEnd}
      >
        <div
          className="ptr-indicator"
          style={{
            opacity: pullState === 'idle' ? 0 : Math.min(1, pullDistance / 40),
            transform: `translateY(${pullDistance}px)`,
          }}
        >
          <div
            className={`ptr-spinner ${pullState === 'refreshing' ? 'spinning' : ''}`}
            style={pullState === 'refreshing' ? undefined : { transform: `rotate(${pullState === 'armed' ? 180 : Math.min(180, pullDistance * 2.8)}deg)` }}
          >
            {pullState === 'refreshing' ? <History size={18} aria-hidden="true" /> : <ArrowDownCircle size={18} aria-hidden="true" />}
          </div>
          <span className="text-[10px] text-ink-3 uppercase tracking-widest">{pullLabel}</span>
        </div>

        <div
          style={{
            transform: `translateY(${pullDistance}px)`,
            transition: pullState === 'idle' || pullState === 'refreshing' ? 'transform 220ms cubic-bezier(.2,.8,.2,1)' : 'none',
          }}
        >
          <TrustManifesto onRules={() => setRoute('rules')} />

          <MilharLadder onOpen={(tier) => setRoute(tier === '5' ? 'milhar.hora' : 'milhar')} />

          <button
            type="button"
            onClick={() => setRoute('sorteios')}
            className="ds-sorteios tap"
            data-lobby-sorteios="true"
          >
            <span className="ds-sorteios-badge" aria-hidden="true"><Ticket size={20} /></span>
            <span className="ds-sorteios-body">
              <span className="ds-sorteios-titlerow">
                <span className="ds-sorteios-title">{t('sorteiosTitle')}</span>
                <span className="ds-sorteios-free">{t('sorteiosFreeBadge')}</span>
              </span>
              <span className="ds-sorteios-sub">
                {raffleTeaserParts
                  ? <>{raffleTeaserParts[0]}<b>{fmtMoney(rafflePool)}</b>{raffleTeaserParts[1] ?? ''}</>
                  : t('sorteiosLobbyHint')}
              </span>
            </span>
            <ArrowRight size={17} className="ds-sorteios-arrow" aria-hidden="true" />
          </button>

          <LobbyRankingEntry />
          <LobbyFollowsEntry />

          <h1 className="sr-only">{t('lobbyChooseGame')}</h1>

          {roomsQuery.isLoading ? (
            <div className="px-5 py-12 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-brand-500/30 border-t-brand-500" style={{ animation: 'ptrSpin 0.8s linear infinite' }} />
              <div className="text-sm font-bold text-ink-1">{t('loadingRooms')}</div>
            </div>
          ) : null}
          {roomsQuery.isError ? (
            <div className="px-5 py-10 text-center">
              <div className="w-11 h-11 mx-auto mb-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 flex items-center justify-center"><AlertTriangle size={22} aria-hidden="true" /></div>
              <div className="text-sm font-bold text-ink-1">{t('roomLoadError')}</div>
              <div className="text-[12px] text-ink-3 mt-1 max-w-[280px] mx-auto">{t('roomLoadErrorBody')}</div>
              <button className="mt-4 px-4 py-2 rounded-lg bg-brand-600/20 border border-brand-500/30 text-brand-300 text-sm font-semibold tap" type="button" onClick={() => roomsQuery.refetch()}>{t('retry')}</button>
            </div>
          ) : null}
          {!roomsQuery.isLoading && !roomsQuery.isError && groupedRooms.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <div className="w-11 h-11 mx-auto mb-3 rounded-xl bg-card2 border border-border1 text-ink-3 flex items-center justify-center"><LayoutGrid size={22} aria-hidden="true" /></div>
              <div className="text-sm font-bold text-ink-1">{t('noRooms')}</div>
              <div className="text-[12px] text-ink-3 mt-1 max-w-[280px] mx-auto">{t('noRoomsBody')}</div>
              <button className="mt-4 px-4 py-2 rounded-lg bg-card2 border border-border1 text-ink-2 text-sm font-semibold tap" type="button" onClick={() => roomsQuery.refetch()}>{t('retry')}</button>
            </div>
          ) : null}

          {!roomsQuery.isError ? (
            <div className="ds-game-grid">
              {groupedRooms.map((group) => (
                <GameChoiceCard
                  key={group.game}
                  game={group.game}
                  roomCount={group.rooms.length}
                  trail={trailRounds}
                  trailLoading={trailQuery.isLoading}
                  online={presenceQuery.data?.games[group.game]}
                  presenceUpdatedAt={presenceQuery.dataUpdatedAt}
                  presenceLoading={presenceQuery.isLoading}
                  presenceStale={presenceQuery.isError || presenceQuery.isRefetchError}
                  onChoose={() => setSelectedGame(group.game)}
                  onViewChart={() => openGameChart(group.game)}
                />
              ))}
            </div>
          ) : null}

          {!roomsQuery.isLoading && !roomsQuery.isError && groupedRooms.length > 0 ? (
            <>
              <button className="ds-endlink tap" type="button" onClick={openArchiveList}>{t('viewAllDraws')} ›</button>
              <div className="ds-comply"><b>18+</b><span>{t(marketCopyKey('complyLine', countryCode))}</span></div>
            </>
          ) : null}
          <div className="h-4" />
        </div>
      </div>
      {guestMode && !landingSeen ? (
        <LandingIntro
          trail={trailRounds}
          onDismiss={(goRules) => {
            setLandingSeen(true);
            writeStorage(typeof window === 'undefined' ? undefined : window.localStorage, LANDING_SEEN_KEY, '1');
            if (goRules) setRoute('rules');
          }}
        />
      ) : null}
    </section>
  );
}

const ROULETTE_RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);

function userHasBet(user: PlayerUser | null | undefined): boolean {
  return Number(user?.total_effective_bet_brl ?? user?.totalEffectiveBet ?? 0) > 0;
}

function heroTrailChips(trail: ArchiveRound[], onVerify?: (roundNumber: number) => void) {
  return trail.map((round) => {
    const digit = round.games?.lastdigit;
    if (digit == null) return null;
    const cls = `ds-pk ${digit % 2 ? 'o' : 'e'}${onVerify ? ' tap' : ''}`;
    return onVerify ? (
      <span
        key={round.round_number}
        role="button"
        tabIndex={0}
        className={cls}
        onClick={() => onVerify(round.round_number)}
      >
        {digit}
      </span>
    ) : (
      <span key={round.round_number} className={cls}>{digit}</span>
    );
  });
}

// New / guest / never-bet players get the full pitch; players who have already
// been through bet→draw→payout only see the ongoing proof (live trail + verify),
// so the trust block never turns into a lecture they scroll past every visit.
// Results live once per game section now, so the hero is purely the trust
// statement + a "verify it yourself" CTA — no results ticker competing with
// the per-game strips below.
function TrustHero({ veteran, onRules }: {
  veteran: boolean;
  onRules: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const countryCode = useCountryStore((state) => state.country.code);

  if (veteran) {
    return (
      <div className="ds-hero-mini" data-trust-hero="collapsed">
        <span className="ds-hero-dot" />
        <span className="ds-hero-mini-lb">{t('trustStrip')}</span>
        <button className="ds-hero-mini-more tap" type="button" onClick={onRules}>{t('podeConferir')} ›</button>
      </div>
    );
  }

  return (
    <section className="ds-hero" data-trust-hero="full">
      <div className="ds-hero-eye"><span className="ds-hero-dot" />{t('trustStrip')}</div>
      <h2 className="ds-hero-title">{t('heroTitle')}</h2>
      <div className="ds-hero-badges">
        <span className="ds-hb pub">{t('heroPublic')}</span>
        <span className="ds-hb pix">{t(marketCopyKey('heroPix', countryCode))}</span>
        <span className="ds-hb chk">{t('heroCheck')}</span>
      </div>
      <button className="ds-hero-foot tap" type="button" onClick={onRules}>{t('podeConferir')} ›</button>
    </section>
  );
}

// Degrau 1 — first-touch landing for guests: the promise front-and-center with
// live proof, shown once per device. Reuses the hero copy; two ways out.
export function LandingIntro({ trail, onDismiss }: { trail: ArchiveRound[]; onDismiss: (goRules: boolean) => void }) {
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  useI18nStore((state) => state.language);
  const chips = heroTrailChips(trail).filter(Boolean);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, () => onDismiss(false));
  return (
    <div ref={dialogRef} className="ds-landing" role="dialog" aria-modal="true" aria-label={t('heroTitle')}>
      <div className="ds-landing-inner">
        <div className="ds-landing-brand">
          <BrandOwl size={38} />
          <BrandWordmark height={19} />
        </div>
        <div className="ds-hero-eye"><span className="ds-hero-dot" />{t('trustStrip')}</div>
        <h1 className="ds-landing-title">{t('heroTitle')}</h1>
        <p className="ds-landing-sub">{t(marketCopyKey('landingSub', countryCode))}</p>
        <div className="ds-hero-badges ds-landing-badges">
          <span className="ds-hb pub">{t('heroPublic')}</span>
          <span className="ds-hb pix">{t(marketCopyKey('heroPix', countryCode))}</span>
          <span className="ds-hb chk">{t('heroCheck')}</span>
        </div>
        {chips.length > 0 ? (
          <div className="ds-landing-proof">
            <span className="ds-hero-plb">{t('ultimosLabel')}</span>
            <div className="ds-landing-trail">{chips}</div>
          </div>
        ) : null}
        <div className="ds-landing-cta">
          <button data-autofocus className="ds-btn ds-btn-p ds-landing-go tap" type="button" onClick={() => onDismiss(false)}>{t('lobbySeeGames')} →</button>
          <button className="ds-btn ds-btn-t tap" type="button" onClick={() => onDismiss(true)}>{t('howItWorksBar')}</button>
        </div>
        <p className="ds-landing-legal"><b>18+</b> {t(marketCopyKey('complyLine', countryCode))}</p>
      </div>
    </div>
  );
}

/**
 * The gaming day to ask the archive for, in the anchor zone.
 *
 * This is a DATA key, not a heading: `#001..#288` is numbered in the anchor zone,
 * so the day of rounds that carries a full 001-288 run is the anchor's day. It
 * used to hardcode `America/Sao_Paulo`; it now follows the active country, so an
 * Argentine lobby asks for Argentina's day.
 */
export function brTodayDate(): string {
  const zone = roundAnchorTimeZone();
  try {
    return new Date().toLocaleDateString('en-CA', zone ? { timeZone: zone } : undefined);
  } catch {
    return new Date().toLocaleDateString('en-CA');
  }
}

// Draw times render in the viewer's own zone — see SorteiosPage's `hhmm`, which
// this must agree with, since the lobby teaser advertises the same draw.
export function hhmmBRT(seconds: number | undefined): string {
  if (!seconds) return '';
  try {
    return new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', hour12: false })
      .format(new Date(seconds * 1000));
  } catch {
    return '';
  }
}

// Top players, live, in the Hall. Tap a face to open their profile, tap the
// header to open the full board — the leaderboard route is unchanged, it just
// gained a high-traffic front door instead of a rarely-tapped tab.
// The lobby speaks in rows: an icon block, a title, a live line, an arrow. The
// winners strip spoke in avatars with rank badges — a social grammar the rest
// of the page does not use — and it sat at the very bottom, under the 18+
// line, which is the least-read place on the screen for something meant to
// pull people into the ranking. It is a row now, directly under Draws, and it
// borrows Draws' own shape: the live figure in the subtitle is the hook, the
// same way Draws advertises the live pool.
function LobbyRankingEntry() {
  const t = useI18nStore((state) => state.t);
  const token = useAuthStore((state) => state.token);
  const setRoute = useAppStore((state) => state.setRoute);
  // Same window, same sort, same threshold the board opens on. This row used to
  // advertise the day's leader while the page ranked the last hour, so the name
  // and the figure changed the moment you tapped it.
  const query = useQuery({
    queryKey: ['leaderboard', token || 'guest', DEFAULT_LEADERBOARD_PERIOD, 'all', 'all', 'profit', 'profit', false, 5],
    queryFn: () => fetchLeaderboard({ token: token || undefined, period: DEFAULT_LEADERBOARD_PERIOD, game: 'all', room: 'all' }),
    staleTime: 60_000,
  });
  const leader = (query.data?.items || [])[0];
  const leaderName = String(leader?.user?.username || leader?.user?.name || '').trim();
  const leaderProfit = Number(leader?.profit || 0);
  // The entry never depends on the data: an empty or still-loading board falls
  // back to the static line rather than collapsing the row, which is how the
  // old strip left a hole in the page whenever the board was quiet.
  const live = Boolean(leaderName && leaderProfit > 0);

  return (
    <button
      type="button"
      onClick={() => setRoute('leaderboard')}
      className="ds-sorteios tap"
      data-lobby-ranking="true"
    >
      <span className="ds-sorteios-badge is-ranking" aria-hidden="true"><Crown size={20} /></span>
      <span className="ds-sorteios-body">
        <span className="ds-sorteios-titlerow">
          <span className="ds-sorteios-title">{t('lobbyRankingTitle')}</span>
          {live ? <span className="ds-sorteios-free is-live">{t('lobbyRankingLive')}</span> : null}
        </span>
        <span className="ds-sorteios-sub">
          {live
            ? <>{t('lobbyRankingLeader').replace('%name%', leaderName)} <b>{fmtMoney(leaderProfit)}</b></>
            : t('lobbyRankingHint')}
        </span>
      </span>
      <ArrowRight size={17} className="ds-sorteios-arrow" aria-hidden="true" />
    </button>
  );
}

// The payoff for following someone. Until now a follow produced nothing the
// player could ever see again — this row is where it comes back: who you follow
// is at a table right now, one tap from their room.
function LobbyFollowsEntry() {
  const t = useI18nStore((state) => state.t);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const setRoute = useAppStore((state) => state.setRoute);
  const setFollowsTargetTab = useAppStore((state) => state.setFollowsTargetTab);
  const query = useQuery({
    queryKey: ['social-following', token || 'guest'],
    queryFn: () => fetchSocialFollowing(token || ''),
    enabled: !!token && !guestMode,
    staleTime: 60_000,
    retry: false,
  });

  const rows = query.data?.items || [];
  // Nothing to say and nothing to act on — a row that only ever reads "0" is
  // clutter on the busiest screen in the app.
  if (rows.length === 0) return null;
  const online = rows.filter((row) => row.online);

  return (
    <button
      type="button"
      onClick={() => { setFollowsTargetTab('following'); setRoute('me.follows'); }}
      className="ds-sorteios tap"
      data-lobby-follows="true"
    >
      <span className="ds-sorteios-badge is-follows" aria-hidden="true"><UsersRound size={20} /></span>
      <span className="ds-sorteios-body">
        <span className="ds-sorteios-titlerow">
          <span className="ds-sorteios-title">{t('lobbyFollowsTitle')}</span>
          {online.length > 0 ? <span className="ds-sorteios-free is-live">{t('lobbyRankingLive')}</span> : null}
        </span>
        <span className="ds-sorteios-sub">
          {online.length > 0
            ? <>{t('lobbyFollowsLive').replace('%n%', String(online.length))} · <b>@{online[0].user?.username || ''}</b></>
            : t('lobbyFollowsIdle').replace('%n%', String(rows.length))}
        </span>
      </span>
      <ArrowRight size={17} className="ds-sorteios-arrow" aria-hidden="true" />
    </button>
  );
}

function TrustManifesto({ onRules }: { onRules: () => void }) {
  const t = useI18nStore((state) => state.t);
  return (
    <button className="ds-manifesto tap" type="button" onClick={onRules}>
      <span className="ds-manifesto-mark"><ShieldCheck size={18} aria-hidden="true" /></span>
      <span className="ds-manifesto-copy">
        <strong>{t('lobbyTrustTitle')}</strong>
        <span>{t('lobbyTrustBody')}</span>
      </span>
      <span className="ds-manifesto-link">
        <span className="sr-only">{t('fairnessTitle')}</span>
        <ArrowRight size={16} aria-hidden="true" />
      </span>
    </button>
  );
}

function GameChoiceCard({
  game,
  roomCount,
  trail,
  trailLoading,
  online,
  presenceUpdatedAt,
  presenceLoading,
  presenceStale,
  onChoose,
  onViewChart,
}: {
  game: GameCode;
  roomCount: number;
  trail: ArchiveRound[];
  trailLoading: boolean;
  online: number | undefined;
  presenceUpdatedAt: number;
  presenceLoading: boolean;
  presenceStale: boolean;
  onChoose: () => void;
  onViewChart: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const chips = trail.map((round) => ({ round, chip: trailChip(game, round) })).filter((item) => item.chip).slice(0, 8);
  const descriptionKey = game === 'lastdigit' ? 'lobbyGameLastdigit' : game === 'sicbo' ? 'lobbyGameSicbo' : 'lobbyGameRoulette';
  const badgeKey = game === 'lastdigit' ? 'lobbyGameEasy' : game === 'sicbo' ? 'lobbyGameOptions' : 'lobbyGameClassic';

  return (
    <article className="ds-game-choice" data-game={game} aria-labelledby={`game-title-${game}`}>
      <button
        className="ds-game-choice-main tap"
        type="button"
        onClick={onChoose}
        aria-label={`${t('lobbyChooseRoomAction')} · ${t(gameNameKey(game))}`}
      >
        <span className="ds-game-choice-icon"><GameIcon game={game} /></span>
        <span className="ds-game-choice-copy">
          <span className="ds-game-choice-title">
            <h3 id={`game-title-${game}`}>{t(gameNameKey(game))}</h3>
            <em>{t(badgeKey)}</em>
          </span>
          <span className="ds-game-choice-desc">{t(descriptionKey)}</span>
          <span className="ds-game-choice-details">
            <span className="ds-game-choice-room-count">
              <LayoutGrid size={12} aria-hidden="true" />
              <span>{roomCount} {t('roomsPlural')}</span>
            </span>
            <GamePresence
              online={online}
              updatedAt={presenceUpdatedAt}
              loading={presenceLoading}
              stale={presenceStale}
            />
          </span>
        </span>
      </button>
      <div className="ds-game-choice-foot">
        <button className="ds-game-results tap" type="button" onClick={onViewChart} aria-label={`${t('analyticsFromBet')} · ${t(gameNameKey(game))}`}>
          <span className="ds-results-label">{t('lobbyRecentResults')} <ArrowRight size={12} aria-hidden="true" /></span>
          <span className="ds-proof-track" aria-hidden="true">
            {chips.map(({ round, chip }) => (
              chip?.dice ? (
                <span key={round.round_number} className="ds-dice-result">
                  {chip.dice.map((value, index) => <MiniDice key={`${round.round_number}-${index}`} value={value} />)}
                </span>
              ) : (
                <span key={round.round_number} className={`ds-pk ${chip?.cls || ''}`}>{chip?.label}</span>
              )
            ))}
            {trailLoading
              ? Array.from({ length: Math.max(0, 8 - chips.length) }, (_, index) => <span key={`loading-${index}`} className="ds-pk ds-pk-loading" />)
              : null}
          </span>
          {!trailLoading && chips.length === 0 ? <span className="ds-results-empty">{t('lobbyResultsUnavailable')}</span> : null}
        </button>
      </div>
    </article>
  );
}

export function GamePresence({
  online,
  updatedAt,
  loading,
  stale,
}: {
  online: number | undefined;
  updatedAt: number;
  loading: boolean;
  stale: boolean;
}) {
  const t = useI18nStore((state) => state.t);

  if (loading && online === undefined) {
    return <span className="ds-presence-loading" aria-label={t('loading')} />;
  }

  if (online === undefined) {
    return (
      <span className="ds-presence ds-presence-stale" aria-label={t('lobbyOnlineUnavailable')} title={t('lobbyOnlineUnavailable')}>
        <span className="ds-presence-dot" aria-hidden="true" />
        <span>{t('lobbyOnlineUnavailableShort')}</span>
      </span>
    );
  }

  return (
    <span
      className={`ds-presence${stale ? ' ds-presence-stale' : ''}`}
      aria-label={t('lobbyOnlineNow').replace('%n%', fmtInt(online))}
    >
      <span className="ds-presence-dot" aria-hidden="true">
        {!stale && updatedAt > 0 ? <span key={updatedAt} className="ds-presence-ping" /> : null}
      </span>
      <span key={online} className="ds-presence-value">
        {t('lobbyOnlineNow').replace('%n%', fmtInt(online))}
      </span>
    </span>
  );
}

// Tier order, low to high — the page reads top-down as "start here, aim there".
const TIER_ORDER = ['bronze', 'silver', 'gold', 'diamond', 'black'];

/**
 * The room picker for one game. One decision — which table — and nothing that
 * competes with it (2026-09-04 review): no public/private tabs, no tier filter,
 * no "live now" banner, no per-room caps. Each tier is a coloured group with
 * its stake range and perk stated once; its rooms are plain rows underneath,
 * busiest first, and the one place the player is most likely to go carries the
 * only solid button.
 */
export function GameRoomDirectory({
  game,
  rooms,
  vipLevel,
  walletBrl,
  guestMode,
  onBack,
  onEnter,
}: {
  game: GameCode;
  rooms: Room[];
  vipLevel: number;
  walletBrl: number;
  guestMode: boolean;
  onBack: () => void;
  onEnter: (room: Room) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const showToast = useAppStore((state) => state.showToast);
  const tierOf = (room: Room) => String(room.tier || 'bronze').toLowerCase();
  const tiers = [...new Set(rooms.map(tierOf))]
    .sort((a, b) => TIER_ORDER.indexOf(a) - TIER_ORDER.indexOf(b) || a.localeCompare(b));

  // Busiest first, then the room with something happening, then a stable code
  // order — the numbering (Room 1, Room 2) stays by code so it does not shuffle.
  const byActivity = (a: Room, b: Room) =>
    roomOnlineCount(b) - roomOnlineCount(a)
    || Number(b.thisRoundBets || 0) - Number(a.thisRoundBets || 0)
    || String(a.code || a.id).localeCompare(String(b.code || b.id));
  const recommended = [...rooms]
    .filter((room) => roomDirectoryAccess(room, guestMode, vipLevel, t).allowed)
    .sort((a, b) => byActivity(a, b)
      || Number(walletBrl >= roomMinimumStake(b)) - Number(walletBrl >= roomMinimumStake(a)))[0];

  function actOnRoom(room: Room) {
    const access = roomDirectoryAccess(room, guestMode, vipLevel, t);
    if (access.action === 'enter') onEnter(room);
    else if (access.action === 'signin') {
      showToast(t('roomDirectorySignInPopup'), 'warn', { ms: 3600 });
    } else if (access.action === 'vip') {
      showToast(t('roomDirectoryVipGatePopup')
        .replace('%vip%', String(roomMinVip(room)))
        .replace('%my%', String(vipLevel)), 'warn', { ms: 4000 });
    } else {
      showToast(t('roomDirectoryUnavailable'), 'warn');
    }
  }

  return (
    <section className="room-directory ds-screen">
      <header className="room-directory-header">
        <button className="room-directory-back tap" type="button" onClick={onBack} aria-label={t('backToLobby')}>
          <ArrowLeft size={19} aria-hidden="true" />
        </button>
        <span className="room-directory-game-icon"><GameIcon game={game} /></span>
        <div>
          <h1>{t(gameNameKey(game))}</h1>
        </div>
      </header>

      <div className="room-directory-scroll no-scrollbar">
        {rooms.length === 0 ? (
          <section className="room-directory-empty">
            <h2>{t('roomDirectoryNoRooms')}</h2>
          </section>
        ) : (
          <div className="rd-tiers">
            {tiers.map((tier) => {
              const tierRooms = rooms.filter((room) => tierOf(room) === tier);
              const numbered = [...tierRooms].sort((a, b) => String(a.code || a.id).localeCompare(String(b.code || b.id)));
              const minStake = Math.min(...tierRooms.map(roomMinimumStake).filter((v) => v > 0));
              const maxStake = Math.max(...tierRooms.map((room) => Number(room.perRoundMaxBrl || 0)));
              const perk = Math.max(...tierRooms.map(roomRakebackPct));
              return (
                <section className={`rd-tier is-${tier}`} key={tier} data-room-tier={tier}>
                  <header className="rd-tier-head">
                    {tier === 'gold' || tier === 'diamond' || tier === 'black'
                      ? <Crown size={17} aria-hidden="true" />
                      : <Medal size={17} aria-hidden="true" />}
                    <h2>{t(`tier_${tier}` as MessageKey)}</h2>
                    {perk > 0 ? <em>{t('roomDirectoryBackBadge').replace('%pct%', fmtPercent(perk))}</em> : null}
                    {Number.isFinite(minStake) && maxStake > 0 ? (
                      <span>{fmtMoney(minStake)} – {fmtMoney(maxStake)}</span>
                    ) : null}
                  </header>
                  {[...tierRooms].sort(byActivity).map((room) => (
                    <RoomDirectoryRow
                      key={room.id}
                      room={room}
                      number={numbered.findIndex((candidate) => candidate.id === room.id) + 1}
                      access={roomDirectoryAccess(room, guestMode, vipLevel, t)}
                      primary={room.id === recommended?.id}
                      onAction={() => actOnRoom(room)}
                    />
                  ))}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// One row per room: name, who is inside, and either the way in or the reason
// you cannot go yet. The whole row is the tap target — a locked row explains
// itself in a toast rather than a dead button.
function RoomDirectoryRow({
  room,
  number,
  access,
  primary,
  onAction,
}: {
  room: Room;
  number: number;
  access: ReturnType<typeof roomDirectoryAccess>;
  primary: boolean;
  onAction: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const locked = !access.allowed;
  return (
    <button
      type="button"
      className={`rd-room tap${primary ? ' is-primary' : ''}${locked ? ' is-locked' : ''}`}
      onClick={onAction}
      data-room-id={room.id}
    >
      <span className="rd-room-name">
        {t('roomDirectoryRoomN').replace('%n%', String(number))}
        <i data-online-count className="rd-room-online"><b /> {fmtInt(roomOnlineCount(room))}</i>
      </span>
      {access.action === 'enter' ? (
        <span className="rd-room-cta">{t('roomDirectoryPlay')}</span>
      ) : access.action === 'vip' ? (
        <span className="rd-room-lock"><Lock size={12} aria-hidden="true" />{t('roomDirectoryVipShort').replace('%vip%', String(roomMinVip(room)))}</span>
      ) : access.action === 'signin' ? (
        <span className="rd-room-lock"><Lock size={12} aria-hidden="true" />{t('login')}</span>
      ) : (
        <span className="rd-room-lock">{t('roomDirectoryUnavailable')}</span>
      )}
    </button>
  );
}

export function roomMinimumStake(room: Room) {
  return Number(room.chipValuesBrl?.[0] || 0);
}

export function roomRakebackPct(room: Room) {
  return Number(room.rakebackPct || 0) * 100;
}

export function roomDirectoryAccess(
  room: Room,
  guestMode: boolean,
  vipLevel: number,
  t: ReturnType<typeof useI18nStore.getState>['t'],
) {
  const tier = String(room.tier || 'bronze').toLowerCase();
  const minVip = roomMinVip(room);
  if (!room.enabled) {
    return { allowed: false, action: 'unavailable' as const, tone: 'muted', label: t('roomDirectoryUnavailable'), cta: t('roomDirectoryUnavailable') };
  }
  if (guestMode) {
    if (tier === 'bronze') {
      return { allowed: true, action: 'enter' as const, tone: 'guest', label: t('roomDirectoryGuestAccess'), cta: t('enterRoom') };
    }
    return { allowed: false, action: 'signin' as const, tone: 'locked', label: t('roomDirectorySignInRequired'), cta: t('login') };
  }
  if (tier !== 'bronze' && vipLevel < minVip) {
    return {
      allowed: false,
      action: 'vip' as const,
      tone: 'locked',
      label: t('roomDirectoryRequiresVip').replace('%vip%', String(minVip)),
      cta: t('roomDirectoryViewVip'),
    };
  }
  return { allowed: true, action: 'enter' as const, tone: 'open', label: t('roomDirectoryAccessGranted'), cta: t('enterRoom') };
}
export function trailChip(game: GameCode, round: ArchiveRound): { label: string; cls: string; dice?: number[] } | null {
  const games = round.games;
  if (!games) return null;
  if (game === 'lastdigit') {
    const d = games.lastdigit;
    if (d == null) return null;
    return { label: String(d), cls: d % 2 === 1 ? 'o' : 'e' };
  }
  if (game === 'sicbo') {
    const dice = games.sicbo;
    if (!dice || dice.length !== 3) return null;
    return { label: dice.join('·'), cls: 'dice', dice };
  }
  const n = games.roulette;
  if (n == null) return null;
  return { label: String(n), cls: n === 0 ? 'grn' : ROULETTE_RED.has(n) ? 'red' : 'blk' };
}

const DICE_PIPS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

export function MiniDice({ value }: { value: number }) {
  const active = new Set(DICE_PIPS[value] || []);
  return (
    <span className="ds-mini-die">
      {Array.from({ length: 9 }, (_, index) => <i key={index} className={active.has(index) ? 'on' : ''} />)}
    </span>
  );
}

function RoomCard({ room, guestMode, vipLevel, onPreview }: { room: Room; guestMode: boolean; vipLevel: number; onPreview: (room: Room) => void }) {
  const t = useI18nStore((state) => state.t);
  const gate = roomGate(room, vipLevel, t);
  const minChip = room.chipValuesBrl?.[0] || 0;
  const tier = String(room.tier || 'bronze').toLowerCase();
  const heat = heatTier(room);
  const host = roomHost(room);
  const openPreview = () => onPreview(room);

  const bets = Number(room.thisRoundBets || 0);
  return (
    <button
      type="button"
      onClick={openPreview}
      className={`ds-card tap ${gate.ok || gate.reason === 'soon' ? '' : 'opacity-80'}`}
      data-room-card-react="true"
      data-room-id={room.id}
    >
      <div className="ds-card-top">
        <div className="ds-glogo">
          <div className="w-full h-full" style={{ padding: '7px' }}><GameIcon game={room.game} /></div>
          {room.enabled ? <span className="ds-dot" /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className="ds-rname">
            <h4>{room.label}</h4>
            <span className={`ds-tier tier-${tier}`}>{t(`tier_${tier}` as MessageKey)}</span>
            {room.enabled && gate.reason === 'vip' ? (
              <span className="ds-lock" title={gate.label}>
                <Lock size={12} aria-hidden="true" />
                <span className="sr-only">{gate.label}</span>
              </span>
            ) : null}
          </div>
          <div className="ds-meta">
            {room.enabled ? (
              <span className="ds-live-mini"><i />{t('liveShort')}</span>
            ) : null}
            <span className="font-mono tabular-nums">{fmtMoney(minChip)} – {fmtMoney(room.perRoundMaxBrl)}</span>
          </div>
        </div>
        {!room.enabled ? (
          <span className="ds-heat q"><i />{t('roomComingSoon')}</span>
        ) : (
          <span className={`ds-heat ${heat === 'hot' ? 'hot' : heat === 'warm' ? 'md' : 'q'}`}>
            <i />
            {heatLabel(room)}
          </span>
        )}
      </div>
      <div className="ds-vol">
        {bets > 0 ? (
          <>
            <b className="font-mono tabular-nums">{fmtInt(bets)}</b> {bets === 1 ? t('betsNowOne') : t('betsNowMany')} ·{' '}
          </>
        ) : null}
        <span data-online-count><b key={`on-${roomOnlineCount(room)}`} className="font-mono tabular-nums ds-num-pulse">{fmtInt(roomOnlineCount(room))}</b> {roomOnlineCount(room) === 1 ? t('peopleNowOne') : t('peopleNowMany')}</span>
      </div>
      {room.preview?.text ? (
        <div className="ds-preview">
          <b>{room.preview.user || t('anonPlayerName')}:</b> {room.preview.text}
        </div>
      ) : host && (host.name || host.username) ? (
        <div className="ds-preview"><b>👑 {host.name || host.username}</b></div>
      ) : null}
      {guestMode ? <span className="sr-only">{t('previewRoom')}</span> : null}
    </button>
  );
}

function RoomIntroSheet({
  room,
  vipLevel,
  guestMode,
  trail,
  onClose,
  onEnter,
}: {
  room: Room;
  vipLevel: number;
  guestMode: boolean;
  trail: ArchiveRound[];
  onClose: () => void;
  onEnter: (room: Room) => void;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const countryCode = useCountryStore((state) => state.country.code);
  const introUser = useAuthStore((state) => state.user);
  const gate = roomGate(room, vipLevel, t);
  const minChip = room.chipValuesBrl?.[0] || 0;
  const maxChip = room.chipValuesBrl?.[room.chipValuesBrl.length - 1] || 0;
  const minVip = roomMinVip(room);
  const perks = roomPerks(room, t, countryCode);
  const host = roomHost(room);
  const tierKey = String(room.tier || 'bronze').toLowerCase();

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className={`profile-sheet room-intro-sheet-react tier-${room.tier}`} role="dialog" aria-modal="true" aria-label={t('roomIntroTitle')} onMouseDown={(event) => event.stopPropagation()} data-room-intro-sheet-react="true" data-room-id={room.id}>
        <button className="w-full px-4 pt-3 pb-2 flex justify-center tap bg-transparent border-0 cursor-pointer" type="button" onClick={onClose} aria-label={t('close')}>
          <div className="w-12 h-1 bg-border1 rounded-full" />
        </button>
        <div className="px-4 pb-2 ds-doorway-scroll">
          {/* Hero: tier-tinted banner with game icon + name (legacy room-intro-sheet.html) */}
          <div className={`rounded-2xl p-4 mb-3 relative overflow-hidden tier-banner tier-${tierKey}`}>
            <div className="flex items-center gap-3 relative z-10">
              <div className="glogo glogo-game room-intro-glogo">
                <div className="w-full h-full" style={{ padding: '8px' }}><GameIcon game={room.game} /></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`tier-badge tier-${tierKey}`}>{t(`tier_${tierKey}` as MessageKey)}</span>
                  {!room.enabled ? <span className="chip bg-black/40 text-ink-3 border-white/10">{t('roomComingSoon')}</span> : null}
                </div>
                <div className="font-bold text-lg text-white mt-0.5">{t(gameNameKey(room.game))} · {room.label}</div>
                {/* People count lives in the live row below when the room is on air — say it once */}
                {room.enabled ? (
                  room.thisRoundBets > 0 ? (
                    <div className="text-[12px] text-white/70 mt-0.5"><span className="font-mono">{room.thisRoundBets} {t('thisRoundBets')}</span></div>
                  ) : null
                ) : (
                  <div data-online-count className="text-[12px] text-white/70 mt-0.5">{roomOnlineCount(room)}/{room.capacity} {t('introOnline')}</div>
                )}
              </div>
            </div>
          </div>

          {/* Live doorway — the "outside the streaming room" moment: real pulse + last results peek */}
          {room.enabled ? (
            <div className="ds-live-row" data-live-row="true">
              <span className="ds-live-dot" />
              <span className="ds-live-lb">{t('liveNow')}</span>
              <span className="ds-live-meta">
                <span data-online-count>{fmtInt(roomOnlineCount(room))} {roomOnlineCount(room) === 1 ? t('peopleNowOne') : t('peopleNowMany')} · </span>{t('drawEvery5')}
              </span>
            </div>
          ) : null}
          {trail.length > 0 ? (
            <div className="ds-trail mb-3" style={{ marginTop: 0 }}>
              <span className="ds-trail-lb">{t('ultimosLabel')}</span>
              {trail.map((round) => {
                const chip = trailChip(room.game, round);
                if (!chip) return null;
                return <span key={round.round_number} className={`ds-pk ${chip.cls}`}>{chip.label}</span>;
              })}
            </div>
          ) : null}

          {/* Game intro — 3-line "what / where from / payout" (DS §4.9 decision-point education) */}
          <div className="card p-3 mb-3" data-game-intro-mini="true">
            <div className="ds-how-row" style={{ paddingTop: 0 }}>
              <span className="ds-how-ico"><Target size={15} aria-hidden="true" /></span>
              <p>{t(`howWhat_${gameHowSuffix(room.game)}` as MessageKey)}</p>
            </div>
            <div className="ds-how-row">
              <span className="ds-how-ico"><Coins size={15} aria-hidden="true" /></span>
              <p>{t(`howPay_${gameHowSuffix(room.game)}` as MessageKey)}</p>
            </div>
          </div>

          {/* Host */}
          {host && (host.name || host.username) ? (
            <div className="card p-3 mb-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-card2 flex items-center justify-center font-semibold text-ink-1 relative">
                <span>{host.initial || (host.name || host.username || 'S')[0]}</span>
                <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-gold-500 text-brand-900 rounded-full flex items-center justify-center"><Crown size={10} aria-hidden="true" /></span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-ink-3">{t('introHost')}</div>
                <div className="font-semibold text-sm text-ink-1">{host.name || host.username} {host.flag || ''}</div>
              </div>
            </div>
          ) : null}

          {/* Limits + Min VIP — label→value rows so amounts never wrap */}
          <div className="card p-3 mb-3">
            <div className="text-[10px] uppercase tracking-widest text-ink-3 mb-1">{t('introLimits')}</div>
            <div className="ds-kv">
              <span className="ds-kv-l">{t('introPerRoundRange')}</span>
              <span className="ds-kv-v">{fmtMoney(minChip)} – {fmtMoney(room.perRoundMaxBrl || maxChip)}</span>
            </div>
            <div className="ds-kv">
              <span className="ds-kv-l"><Lock size={13} className="text-gold-400 shrink-0" aria-hidden="true" /> {t('introMinVip')}</span>
              {minVip > 0 ? (
                <span className={`ds-kv-v ${vipLevel >= minVip ? 'text-emerald-400' : 'text-rose-400'}`}>VIP{minVip}</span>
              ) : (
                <span className="ds-kv-v text-emerald-400">{t('introVipFree')}</span>
              )}
            </div>
            {Number(room.rakebackPct || 0) > 0 ? (
              <div className="ds-kv">
                <span className="ds-kv-l">{t('introCashback')}</span>
                <span className="ds-kv-v text-emerald-400">{(Number(room.rakebackPct) * 100).toLocaleString('pt-BR')}%</span>
              </div>
            ) : null}
            {Number(room.rakebackPct || 0) > 0 && Number(room.rakebackBrlMinStake || 0) > 0 ? (
              <div className="ds-kv-note">{t('introCashbackMin').replace('%v%', fmtMoney(Number(room.rakebackBrlMinStake)))}</div>
            ) : null}
            <div className="ds-kv">
              <span className="ds-kv-l">{t('introPoolCap')}</span>
              <span className="ds-kv-v ds-kv-dim">{fmtAmount(room.poolCapBrl)} BRL</span>
            </div>
          </div>

          {/* Perks */}
          {perks.length > 0 ? (
            <div className="card p-3 mb-3">
              <div className="text-[10px] uppercase tracking-widest text-ink-3 mb-2">{t('introPerks')}</div>
              <ul className="space-y-1.5">
                {perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-[13px] text-ink-1">
                    <Sparkles size={14} className="mt-0.5 text-gold-400 shrink-0" aria-hidden="true" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        {/* Enter — pinned door handle: always on screen regardless of content height */}
        <div className="px-4 ds-doorway-cta">
          <button
            type="button"
            disabled={!gate.ok}
            onClick={() => onEnter(room)}
            className={`w-full py-3.5 rounded-xl font-bold tap ${gate.ok
              ? 'ds-btn-p'
              : 'bg-card2 text-ink-3 border border-border1'}`}
          >
            {gate.ok ? (
              <span className="inline-flex items-center justify-center gap-2">
                <span>{t('introEnter')}</span>
                <ArrowRight size={16} aria-hidden="true" />
              </span>
            ) : gate.reason === 'vip' ? introVipLabel(gate.minVip, vipLevel, t, introUser) : t('introSoon')}
          </button>
        </div>
      </section>
    </div>
  );
}


function gameHowSuffix(game: GameCode): 'ld' | 'sicbo' | 'rou' {
  return game === 'lastdigit' ? 'ld' : game === 'sicbo' ? 'sicbo' : 'rou';
}

export function groupRooms(rooms: Room[]) {
  const groups = new Map<GameCode, Room[]>();
  for (const room of rooms) {
    const list = groups.get(room.game) || [];
    list.push(room);
    groups.set(room.game, list);
  }
  return (['lastdigit', 'roulette', 'sicbo'] as GameCode[])
    .filter((game) => groups.has(game))
    .map((game) => ({ game, rooms: groups.get(game) || [] }));
}


function useDialogFocus(dialogRef: RefObject<HTMLElement | null>, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const lobby = dialog.closest('.lobby-screen');
    const inerted = lobby
      ? Array.from(lobby.children).filter((child) => child instanceof HTMLElement && !child.contains(dialog)) as HTMLElement[]
      : [];

    inerted.forEach((element) => {
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
    });

    const focusableSelector = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const initialFocus = dialog.querySelector<HTMLElement>('[data-autofocus]')
      || dialog.querySelector<HTMLElement>(focusableSelector)
      || dialog;
    if (!dialog.hasAttribute('tabindex')) dialog.tabIndex = -1;
    window.requestAnimationFrame(() => initialFocus.focus());

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialog!.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');
      if (focusable.length === 0) {
        event.preventDefault();
        dialog!.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    dialog.addEventListener('keydown', handleKeyDown);
    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      inerted.forEach((element) => {
        element.inert = false;
        element.removeAttribute('aria-hidden');
      });
      window.requestAnimationFrame(() => previousFocus?.focus());
    };
  }, [dialogRef]);
}

// Legacy tier→minVip fallback (web/data.js TIERS) when the room has no explicit gate.
const TIER_MIN_VIP: Record<string, number> = { bronze: 1, silver: 3, gold: 5, diamond: 7, black: 9 };

function roomMinVip(room: Room) {
  // Backend value wins, including an explicit 0 (= entrada livre). The legacy
  // tier table is only a fallback for rooms that omit the field entirely.
  if (room.minVipLevel !== undefined && room.minVipLevel !== null) return Number(room.minVipLevel);
  return Number(TIER_MIN_VIP[String(room.tier || '').toLowerCase()] || 0);
}

export function roomGate(room: Room, vipLevel: number, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const minVip = roomMinVip(room);
  if (!room.enabled) return { ok: false, reason: 'soon' as const, label: t('roomComingSoon'), minVip };
  if (vipLevel < minVip) return { ok: false, reason: 'vip' as const, label: t('accessLocked').replace('%vip%', String(minVip)), minVip };
  return { ok: true, reason: 'ok' as const, label: 'OK', minVip };
}

function roomPerks(room: Room, t: ReturnType<typeof useI18nStore.getState>['t'], countryCode: string) {
  const perks = [t(marketCopyKey('introPerkFastPix', countryCode))];
  if (room.thisRoundBets >= 8) perks.push(t('introPerkActiveRoom'));
  if (Number(room.minVipLevel || 0) > 0) perks.push(t('introPerkVipRoom'));
  if (roomHost(room)) perks.push(t('perkHost'));
  return perks;
}

function roomHost(room: Room) {
  const host = room.hostUser || room.host_user || null;
  if (host && (host.name || host.username || host.initial)) return host;
  if (room.hostName) return { name: room.hostName, initial: room.hostName[0] };
  if (room.hostUserId) return { name: room.hostUserId === 'u-admin' ? 'Sorteia' : room.hostUserId, initial: room.hostUserId[0] };
  return null;
}

function introVipLabel(minVip: number, vipLevel: number, t: ReturnType<typeof useI18nStore.getState>['t'], user?: PlayerUser | null) {
  // Legacy expToLevel: threshold(target VIP) minus lifetime effective bet.
  const thresholds = (user?.vip_thresholds || {}) as Record<string, number>;
  const need = Number(thresholds[String(minVip)] ?? 0);
  const have = Number(user?.total_effective_bet_brl ?? user?.totalEffectiveBet ?? 0);
  const exp = Math.max(0, need - have);
  return t('introVipShort')
    .replace('%vip%', String(minVip))
    .replace('%my%', String(vipLevel))
    .replace('%exp%', fmtAmount(exp));
}

function heatTier(room: Room) {
  const occupancy = room.capacity ? room.online / room.capacity : 0;
  if (room.thisRoundBets >= 20 || occupancy >= 0.75) return 'hot';
  if (room.thisRoundBets >= 8 || occupancy >= 0.4) return 'warm';
  return 'calm';
}

function heatLabel(room: Room) {
  const t = useI18nStore.getState().t;
  const tier = heatTier(room);
  if (tier === 'hot') return t('hot');
  if (tier === 'warm') return t('active');
  return t('calm');
}

/**
 * 大厅的 Milhar 入口行。视觉规格 §4。
 *
 * 复用 `.ds-sorteios` 的整套骨架，只加 `.ds-milhar` 覆盖 badge tint 与金额排版 ——
 * 与该文件已确立的原则一致：「读起来是兄弟，而不是两套设计」。
 *
 * 金额是**中奖者到手**（池子 × 80%），不是池子总额（master §4.1.1）。
 * 拿不到数据时显示 skeleton，**不显示 R$ 0** —— 空的奖池数字比没有数字更伤。
 */
/**
 * A tier that exists in the ladder design but not in the backend.
 *
 * Rendered as a `div`, not a `button`: there is nothing to open, and a disabled button
 * still invites the tap that then does nothing. Shows the cadence and digit count —
 * which are real, decided facts — and **no prize figure at all**. Borrowing the 4-digit
 * pool here would put a number on screen that is wrong by two orders of magnitude while
 * looking exactly like a live one.
 */
function MilharTierSoonEntry({ tier }: { tier: MilharTier }) {
  const t = useI18nStore((state) => state.t);
  return (
    <div className="ds-sorteios ds-milhar ds-milhar-soon" data-milhar-tier={tier.id} data-milhar-available="false">
      <span className="ds-sorteios-badge ds-milhar-badge" aria-hidden="true">
        <LayoutGrid size={20} />
      </span>
      <span className="ds-sorteios-body">
        <span className="ds-sorteios-titlerow">
          <span className="ds-sorteios-title">{t(tier.titleKey)}</span>
          <span className="ds-milhar-soon-badge">{t('milharTierSoon')}</span>
        </span>
        <span className="ds-sorteios-sub">
          <span className="ds-milhar-soon-cadence">{t(tier.cadenceKey)}</span>
        </span>
      </span>
    </div>
  );
}

/** The whole ladder: live tiers first, then the ones still to come. */
function MilharLadder({ onOpen }: { onOpen: (tier: MilharTierId) => void }) {
  return (
    <>
      {liveMilharTiers().map((tier) => (
        <MilharLobbyEntry key={tier.id} tier={tier} onOpen={() => onOpen(tier.id)} />
      ))}
      {upcomingMilharTiers().map((tier) => (
        <MilharTierSoonEntry key={tier.id} tier={tier} />
      ))}
    </>
  );
}

function MilharLobbyEntry({ tier, onOpen }: { tier: MilharTier; onOpen: () => void }) {
  const t = useI18nStore((state) => state.t);
  const token = useAuthStore((state) => state.token);
  const countryCode = useCountryStore((state) => state.country.code);
  const query = useQuery({
    queryKey: ['milhar-lobby', tier.id, token || 'guest', countryCode],
    queryFn: () => fetchMilharStatus(token, tier.id),
    staleTime: 15_000,
  });
  if (!query.data?.enabled) return null;
  const pool = query.data?.pool;

  return (
    <button type="button" onClick={onOpen} className="ds-sorteios ds-milhar tap"
      data-lobby-milhar="true" data-milhar-tier={tier.id}>
      <span className="ds-sorteios-badge ds-milhar-badge" aria-hidden="true">
        <LayoutGrid size={20} />
      </span>
      <span className="ds-sorteios-body">
        <span className="ds-sorteios-titlerow">
          <span className="ds-sorteios-title">{t(tier.titleKey)}</span>
          <span className="ds-sorteios-free">{t(tier.cadenceKey)}</span>
        </span>
        <span className="ds-sorteios-sub">
          {pool ? (
            <>
              {/* The API resolves the account's independent Milhar market and the pool
                  remains the source of truth for its explicit currency. */}
              <b className="ds-milhar-amount mono">{fmtCurrency(Number(pool.takeHome), pool.currency)}</b>
              {/* 累积尚未开始时连分隔点一起不渲染 —— 见 poolRoundsLine 注释 */}
              {poolRoundsLine(pool.roundsAccumulating, t('milharPoolRounds')) ? (
                <>
                  <span className="ds-milhar-sep">·</span>
                  {poolRoundsLine(pool.roundsAccumulating, t('milharPoolRounds'))}
                </>
              ) : null}
            </>
          ) : (
            <span className="ds-milhar-skeleton sk" />
          )}
        </span>
      </span>
      <ArrowRight size={17} className="ds-sorteios-arrow" aria-hidden="true" />
    </button>
  );
}
